import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Badge } from 'components/ui';
import { useUIStore } from 'stores';
import { getTranslation } from 'i18n';
import { STATUS_CONFIG, STATUS_DOT_COLOR } from '../../types/sales';
import LeadCard from './LeadCard';
import type { SalesLead, SalesLeadStatus } from '../../types/sales';

interface KanbanColumnProps {
  status: SalesLeadStatus;
  leads: SalesLead[];
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({ status, leads }) => {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);
  const config = STATUS_CONFIG[status];
  const dotColor = STATUS_DOT_COLOR[status];

  return (
    <div
      ref={setNodeRef}
      className={`
        flex flex-col flex-1 min-w-[260px] basis-0 rounded-xl transition-colors duration-150
        ${isOver ? 'bg-surface-primary/60' : 'bg-transparent'}
      `}
    >
      <div className="flex items-center gap-2 px-3 py-2.5 mb-1">
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span className={`text-xs font-semibold uppercase tracking-wider ${config.color}`}>
          {t(config.labelKey)}
        </span>
        <Badge variant="neutral" size="sm">{leads.length}</Badge>
      </div>

      <SortableContext id={status} items={leads.map((l) => l.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2 px-1.5 pb-4 min-h-[120px]">
          {leads.length === 0 && (
            <div className="text-xs text-content-muted text-center py-8 italic">{t('salesKanbanNoLeads')}</div>
          )}
          {leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
};

export default React.memo(KanbanColumn);
